"use client";
import { IconLocationPin } from "@repo/assets/images";
import { SectionHeading, StickyButtonWrapper } from "@repo/components";
import {
  AddressCard,
  AddressForm,
} from "@repo/components/account/address-book";
import { INITIAL_ADDRESS_FORM_VALUES } from "@repo/constants/account";
import { Button, OffCanvasDrawer } from "@repo/ui";
import cx from "classnames";
import { useTranslations } from "next-intl";
import { Fragment, useCallback, useState } from "react";

const AddressBook = ({
  address,
  selectedAddress,
  handleSelectedAddress,
  isCheckout,
  session,
  handleAddressComplete,
}) => {
  const t = useTranslations("Common");
  const [openAddressForm, setOpenAddressForm] = useState(false);
  const [formData, setFormData] = useState(INITIAL_ADDRESS_FORM_VALUES);
  const [isEdit, setIsEdit] = useState(false);

  const handleAddressDrawer = useCallback(() => {
    setOpenAddressForm(!openAddressForm);
  }, [openAddressForm]);

  const handleEditAddressForm = useCallback((formValues) => {
    setOpenAddressForm(true);
    setFormData(formValues);
    setIsEdit(true);
  }, []);

  const handleAddAddress = useCallback(() => {
    setOpenAddressForm(true);
    setFormData(INITIAL_ADDRESS_FORM_VALUES);
    setIsEdit(false);
  }, []);

  return (
    <>
      <div
        className={cx({
          "hidden md:block": !isCheckout,
        })}
      >
        <SectionHeading
          title={t(`${isCheckout ? "Shipping_Address" : "Address_Book"}`)}
        >
          <button
            size="md"
            className="text-anchor-text flex items-center text-sm"
            onClick={handleAddAddress}
          >
            {<span className="pe-2 text-xl">+</span>}{" "}
            <span className="font-body">{t("Add_New_Address")}</span>
          </button>
        </SectionHeading>
      </div>
      <div
        className={cx("border-supportiveDarkGray md:border md:px-4", {
          "pt-4": isCheckout,
          "py-4": !isCheckout,
        })}
      >
        <div className="text-darkgray grid grid-cols-1 gap-4 md:grid-cols-2">
          {address?.map((item) => (
            <Fragment key={item?.id}>
              <AddressCard
                item={item}
                handleSelectedAddress={handleSelectedAddress}
                selectedAddress={selectedAddress}
                isCheckout={isCheckout}
                session={session}
                handleEditAddressForm={handleEditAddressForm}
              />
            </Fragment>
          ))}
          <OffCanvasDrawer
            open={openAddressForm}
            handleClose={handleAddressDrawer}
            title={isEdit ? t("Edit_Address") : t("Add_New_Address")}
            position="right"
          >
            <AddressForm
              setOpenAddressForm={setOpenAddressForm}
              isCheckout={isCheckout}
              session={session}
              formData={formData}
              isEdit={isEdit}
              handleAddressDrawer={handleAddressDrawer}
            />
          </OffCanvasDrawer>
        </div>
        {!address?.length && (
          <div
            className={cx("mx-auto w-72 text-center", {
              "py-20": isCheckout,
              "pb-4 pt-14": !isCheckout,
            })}
          >
            <IconLocationPin className="text-icon-color fill-icon-bg mx-auto" />
            <p className="font-heading rtl:font-arabic text-xl font-medium">
              {t("No_Address_Added")}
            </p>
            <p className="text-supportiveGray4 text-base font-medium tracking-wide">
              {t("No_Addresses_Added_Message")}
            </p>
          </div>
        )}
        {isCheckout && (
          <StickyButtonWrapper>
            <Button
              size="md"
              onClick={handleAddressComplete}
              variant="primary"
              disabled={selectedAddress === null}
              className="w-full md:w-auto"
            >
              <span className="flex items-center justify-center">
                {t("Continue")}
              </span>
            </Button>
          </StickyButtonWrapper>
        )}
        {!isCheckout && (
          <div className="flex justify-center md:hidden">
            <Button
              size="md"
              variant="primary"
              className="my-4 w-full"
              onClick={handleAddAddress}
            >
              <span className="font-body">{t("Add_New_Address")}</span>
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default AddressBook;
